import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Stack, Button, MenuItem, Snackbar, Alert, Switch, FormControlLabel,
  IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useSalonContextStore } from '../../store/salonContextStore';
import { employeeApi, simulationApi } from '../../services/employeeService';
import { PageHeader, SectionCard, StatTile, FieldGrid, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';
import { formatMoney, formatPercent } from '../../utils/format';

interface Staff { id: string; firstName: string; lastName: string; }
interface SavedSimConfig {
  useBillableTime?: boolean;
  baseMonthly?: { amount?: number };
  hourly?: { rate?: number };
  serviceCommission?: { pct?: number; materialReducesBase?: boolean };
  productCommission?: { pct?: number };
  bonus?: { amount?: number };
  deductions?: { amount?: number };
  employeeZus?: { pct?: number };
  employerZus?: { pct?: number };
  otherEmployerCost?: { amount?: number };
}
interface SavedSim {
  id: string; name: string; year: number; month: number;
  // Backend zapisuje w kolumnie `inputs` obiekt { config, inputs }
  inputs?: { config?: SavedSimConfig; inputs?: Record<string, number> } | null;
}

const num = (v: string) => Number(v) || 0;

const DEFAULT_INPUTS = { hours: '160', clients: '80', servicesRevenue: '12000', productsRevenue: '2000', materialsCost: '1000' };
const DEFAULT_CFG = {
  baseMonthly: '0', hourlyRate: '25', servicePct: '15', productPct: '10', bonus: '0', deductions: '0',
  employeeZusPct: '13.71', employerZusPct: '20.48', otherEmployerCost: '0', useBillable: true, materialReducesBase: false,
};

const INPUT_FIELDS = [
  ['hours', 'Godziny pracy', 'h'], ['clients', 'Klienci', 'os.'], ['servicesRevenue', 'Sprzedaż usług', 'zł'],
  ['productsRevenue', 'Sprzedaż produktów', 'zł'], ['materialsCost', 'Koszt materiałów', 'zł'],
] as const;
const CFG_FIELDS = [
  ['baseMonthly', 'Podstawa miesięczna', 'zł'], ['hourlyRate', 'Stawka godzinowa', 'zł/h'],
  ['servicePct', 'Prowizja od usług', '%'], ['productPct', 'Prowizja od produktów', '%'],
  ['bonus', 'Bonus', 'zł'], ['deductions', 'Potrącenia', 'zł'],
  ['employeeZusPct', 'ZUS pracownika', '%'], ['employerZusPct', 'ZUS pracodawcy', '%'],
  ['otherEmployerCost', 'Inne koszty pracodawcy', 'zł'],
] as const;

const Subheading: React.FC<{ children: React.ReactNode; first?: boolean }> = ({ children, first }) => (
  <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1.5, ...(first ? {} : { mt: 3, pt: 2.5, borderTop: `1px solid ${tokens.line}` }) }}>
    {children}
  </Typography>
);

const unitAdornment = (unit: string) => ({ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> });

export const SimulatorPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const confirm = useConfirm();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [staffId, setStaffId] = useState('');
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);

  const [inputs, setInputs] = useState({ ...DEFAULT_INPUTS });
  const [cfg, setCfg] = useState({ ...DEFAULT_CFG });
  const [result, setResult] = useState<Record<string, number> | null>(null);
  const [simName, setSimName] = useState('Wariant podstawowy');
  const [saved, setSaved] = useState<SavedSim[]>([]);
  const [selectedSimId, setSelectedSimId] = useState('');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!activeSalonId) return;
    employeeApi.list(activeSalonId).then((rows: Staff[]) => { setStaff(rows); if (rows[0]) setStaffId(rows[0].id); });
  }, [activeSalonId]);

  const loadSaved = useCallback(async () => {
    if (!staffId) { setSaved([]); return; }
    try { setSaved(await simulationApi.list(staffId) as SavedSim[]); } catch { setSaved([]); }
  }, [staffId]);
  useEffect(() => { setSelectedSimId(''); loadSaved(); }, [loadSaved]);

  const applySaved = (id: string) => {
    setSelectedSimId(id);
    if (!id) return;
    const sim = saved.find((s) => s.id === id);
    if (!sim) return;
    const c = sim.inputs?.config;
    const i = sim.inputs?.inputs;
    if (i) {
      setInputs({
        hours: String(i.hours ?? 0), clients: String(i.clients ?? 0), servicesRevenue: String(i.servicesRevenue ?? 0),
        productsRevenue: String(i.productsRevenue ?? 0), materialsCost: String(i.materialsCost ?? 0),
      });
    }
    if (c) {
      setCfg({
        baseMonthly: String(c.baseMonthly?.amount ?? 0), hourlyRate: String(c.hourly?.rate ?? 0),
        servicePct: String(c.serviceCommission?.pct ?? 0), productPct: String(c.productCommission?.pct ?? 0),
        bonus: String(c.bonus?.amount ?? 0), deductions: String(c.deductions?.amount ?? 0),
        employeeZusPct: String(c.employeeZus?.pct ?? 0), employerZusPct: String(c.employerZus?.pct ?? 0),
        otherEmployerCost: String(c.otherEmployerCost?.amount ?? 0),
        useBillable: c.useBillableTime ?? true, materialReducesBase: c.serviceCommission?.materialReducesBase ?? false,
      });
    }
    setSimName(sim.name);
    setMonth(`${sim.year}-${String(sim.month).padStart(2, '0')}`);
  };

  const newVariant = () => {
    setSelectedSimId('');
    setInputs({ ...DEFAULT_INPUTS });
    setCfg({ ...DEFAULT_CFG });
    setSimName('Nowy wariant');
  };

  const deleteSaved = async () => {
    if (!selectedSimId) return;
    const sim = saved.find((s) => s.id === selectedSimId);
    const ok = await confirm({
      title: 'Usunąć zapisany wariant?',
      details: sim ? <><strong>{sim.name}</strong> · {String(sim.month).padStart(2, '0')}/{sim.year}</> : undefined,
      description: 'Wariant zniknie z listy. Nie wpływa to na rzeczywiste rozliczenia.',
      confirmLabel: 'Usuń wariant',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await simulationApi.remove(selectedSimId);
      setSelectedSimId('');
      setToast({ msg: 'Wariant usunięty', sev: 'success' });
      loadSaved();
    } catch {
      setToast({ msg: 'Nie udało się usunąć wariantu. Spróbuj ponownie.', sev: 'error' });
    }
  };

  const buildConfig = () => ({
    useBillableTime: cfg.useBillable,
    baseMonthly: { active: num(cfg.baseMonthly) > 0, amount: num(cfg.baseMonthly) },
    hourly: { active: num(cfg.hourlyRate) > 0, rate: num(cfg.hourlyRate) },
    serviceCommission: { active: num(cfg.servicePct) > 0, pct: num(cfg.servicePct), base: 'GROSS', materialReducesBase: cfg.materialReducesBase },
    productCommission: { active: num(cfg.productPct) > 0, pct: num(cfg.productPct) },
    bonus: { active: num(cfg.bonus) > 0, amount: num(cfg.bonus) },
    deductions: { active: num(cfg.deductions) > 0, amount: num(cfg.deductions) },
    employeeZus: { active: num(cfg.employeeZusPct) > 0, pct: num(cfg.employeeZusPct) },
    employerZus: { active: num(cfg.employerZusPct) > 0, pct: num(cfg.employerZusPct) },
    otherEmployerCost: { active: num(cfg.otherEmployerCost) > 0, amount: num(cfg.otherEmployerCost) },
  });
  const buildInputs = () => ({
    hours: num(inputs.hours), clients: num(inputs.clients), servicesRevenue: num(inputs.servicesRevenue),
    productsRevenue: num(inputs.productsRevenue), materialsCost: num(inputs.materialsCost),
  });

  const run = async () => {
    setResult(await simulationApi.run(buildConfig(), buildInputs()) as Record<string, number>);
  };
  useEffect(() => { run(); /* live recompute */ // eslint-disable-next-line
  }, [inputs, cfg]);

  const save = async () => {
    if (!staffId) return;
    const [y, m] = month.split('-').map(Number);
    try {
      const created = await simulationApi.save({ staffId, name: simName, year: y, month: m, config: buildConfig(), inputs: buildInputs() }) as { id?: string };
      setToast({ msg: 'Symulacja zapisana (bez wpływu na rzeczywiste rozliczenie)', sev: 'success' });
      await loadSaved();
      if (created?.id) setSelectedSimId(created.id);
    } catch (e) {
      setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać symulacji. Spróbuj ponownie.', sev: 'error' });
    }
  };

  const money = (v?: number) => (v != null ? formatMoney(v, { decimals: 2 }) : '—');
  const pct = (v?: number) => (v != null ? formatPercent(v, 1) : '—');

  const details: { label: string; value: string; color?: string; hint?: string }[] = [
    { label: 'Wynagrodzenie brutto', value: money(result?.employeeGross) },
    { label: 'Obrót pracownika', value: money(result?.revenue) },
    { label: 'Koszt materiałów', value: money(result?.materialsCost) },
    { label: 'Rentowność', value: pct(result?.profitabilityPct), hint: 'Marża salonu jako procent obrotu' },
    { label: 'Udział wynagrodzenia w obrocie', value: pct(result?.wageShareOfRevenuePct) },
    { label: 'Obrót na godzinę', value: money(result?.revenuePerHour) },
    { label: 'Minimalny obrót dla rentowności', value: money(result?.minRevenueForProfit), color: tokens.warningText, hint: 'Poniżej tej kwoty pracownik kosztuje więcej, niż zarabia dla salonu' },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Wynagrodzenia' }]}
        title="Symulator wynagrodzeń"
        description="Sprawdź, ile zarobi pracownik i ile zostanie salonowi przy różnych stawkach. Symulacje nie zmieniają rzeczywistych rozliczeń."
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            select
            size="small"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            inputProps={{ 'aria-label': 'Pracownik' }}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>)}
          </TextField>
          <TextField
            type="month"
            size="small"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            inputProps={{ 'aria-label': 'Miesiąc symulacji' }}
            sx={{ width: 190 }}
          />
        </Stack>
      </PageHeader>

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} md={6} lg={7}>
          <SectionCard title="Parametry symulacji">
            <Stack direction="row" gap={1} alignItems="flex-end" flexWrap="wrap" sx={{ mb: 1 }}>
              <TextField
                select
                label="Zapisany wariant"
                value={selectedSimId}
                onChange={(e) => applySaved(e.target.value)}
                SelectProps={{ displayEmpty: true }}
                sx={{ flex: '1 1 220px', minWidth: 0 }}
              >
                <MenuItem value="">Wybierz zapisany wariant</MenuItem>
                {saved.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name} ({String(s.month).padStart(2, '0')}/{s.year})</MenuItem>
                ))}
              </TextField>
              <Stack direction="row" gap={1} alignItems="center">
                <Tooltip title="Usuń wybrany wariant">
                  <span>
                    <IconButton color="error" disabled={!selectedSimId} onClick={deleteSaved} aria-label="Usuń wybrany wariant" sx={{ width: 46, height: 46 }}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={newVariant}>Nowy wariant</Button>
              </Stack>
            </Stack>
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              {saved.length === 0
                ? 'Brak zapisanych wariantów dla tego pracownika — ustaw parametry i zapisz symulację.'
                : 'Wybierz wariant, aby wczytać jego dane, albo zacznij nowy.'}
            </Typography>

            <Subheading>Dane wejściowe</Subheading>
            <FieldGrid columns={2}>
              {INPUT_FIELDS.map(([k, l, u]) => (
                <TextField
                  key={k}
                  fullWidth
                  label={l}
                  type="number"
                  value={inputs[k]}
                  onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })}
                  InputProps={unitAdornment(u)}
                  inputProps={{ min: 0, inputMode: 'decimal' }}
                />
              ))}
            </FieldGrid>

            <Subheading>Zasady wynagrodzenia</Subheading>
            <FieldGrid columns={2}>
              {CFG_FIELDS.map(([k, l, u]) => (
                <TextField
                  key={k}
                  fullWidth
                  label={l}
                  type="number"
                  value={cfg[k] as string}
                  onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                  InputProps={unitAdornment(u)}
                  inputProps={{ min: 0, inputMode: 'decimal' }}
                />
              ))}
            </FieldGrid>
            <Stack gap={1} sx={{ mt: 2.5 }}>
              <FormControlLabel control={<Switch checked={cfg.useBillable} onChange={(e) => setCfg({ ...cfg, useBillable: e.target.checked })} />} label="Rozliczaj według czasu rozliczeniowego" />
              <FormControlLabel control={<Switch checked={cfg.materialReducesBase} onChange={(e) => setCfg({ ...cfg, materialReducesBase: e.target.checked })} />} label="Materiał obniża podstawę prowizji" />
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6} lg={5} sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
          <SectionCard title="Wynik symulacji" subtitle="Przeliczany na bieżąco przy każdej zmianie">
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <StatTile tone="hero" label="Wynagrodzenie netto" value={money(result?.employeeNet)} sx={{ gridColumn: '1 / -1' }} />
              <StatTile label="Całkowity koszt" value={money(result?.totalEmployerCost)} hint="Brutto, ZUS pracodawcy, inne koszty i materiały" />
              <StatTile
                label="Marża salonu"
                value={money(result?.salonMargin)}
                valueColor={result && result.salonMargin < 0 ? tokens.danger : tokens.success}
                hint="Obrót minus wszystkie koszty"
              />
            </Box>

            <Stack sx={{ mt: 2 }} divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
              {details.map((d) => (
                <Stack key={d.label} direction="row" alignItems="baseline" justifyContent="space-between" gap={2} sx={{ py: 1.25 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft }}>{d.label}</Typography>
                    {d.hint && <Typography variant="caption" component="div">{d.hint}</Typography>}
                  </Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: d.color || tokens.ink }}>
                    {d.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ mt: 2, pt: 2.5, borderTop: `1px solid ${tokens.line}` }}>
              <TextField label="Nazwa wariantu" value={simName} onChange={(e) => setSimName(e.target.value)} fullWidth />
              <Button variant="contained" onClick={save} disabled={!staffId} sx={{ flexShrink: 0 }}>Zapisz symulację</Button>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default SimulatorPage;
